/**
 * Banded standing water for a tiled field-drainage channel.
 *
 * This follows puddle's stepped toon falloff and polygon-depth bias: alpha is
 * not a renderer feature, and lifting a water film would violate the ground
 * contract while still z-fighting at range. The X ends are deliberately straight
 * and flush; a lobed end would leave a visible break where channel sections butt.
 */
import * as THREE from 'three';
import { mulberry32 } from '@/audio/prng';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

const SEGMENTS = 8;
const HALF_LENGTH = 2;
const EDGE_HALF_WIDTH = 1.2;
const CENTRE_RISE = 0.006;
const RIM_RISE = 0.0005;

export interface DitchWaterOptions {
  readonly seed?: number;
}

function wetMaterial(kind: 'wet' | 'organic', colour: number): THREE.Material {
  const material = surface(kind, colour);
  material.polygonOffset = true;
  material.polygonOffsetFactor = -2;
  material.polygonOffsetUnits = -4;
  return material;
}

function rise(t: number): number {
  return RIM_RISE + (CENTRE_RISE - RIM_RISE) * (1 - t * t);
}

function stripGeometry(left: readonly THREE.Vector3[], right: readonly THREE.Vector3[]): THREE.BufferGeometry {
  const positions: number[] = [];
  for (let index = 0; index < SEGMENTS; index += 1) {
    const leftHere = left[index];
    const leftNext = left[index + 1];
    const rightHere = right[index];
    const rightNext = right[index + 1];
    if (!leftHere || !leftNext || !rightHere || !rightNext) throw new Error('ditch-water: missing strip point');
    positions.push(...leftHere.toArray(), ...rightNext.toArray(), ...rightHere.toArray());
    positions.push(...leftHere.toArray(), ...leftNext.toArray(), ...rightNext.toArray());
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  return geometry;
}

function meniscusGeometry(left: readonly THREE.Vector3[], right: readonly THREE.Vector3[]): THREE.BufferGeometry {
  const positions: number[] = [];
  for (let index = 0; index < SEGMENTS; index += 1) {
    const leftHere = left[index];
    const leftNext = left[index + 1];
    const rightHere = right[index];
    const rightNext = right[index + 1];
    if (!leftHere || !leftNext || !rightHere || !rightNext) throw new Error('ditch-water: missing meniscus point');
    const middleHere = new THREE.Vector3((leftHere.x + rightHere.x) / 2, CENTRE_RISE, 0);
    const middleNext = new THREE.Vector3((leftNext.x + rightNext.x) / 2, CENTRE_RISE, 0);
    positions.push(...leftHere.toArray(), ...middleNext.toArray(), ...middleHere.toArray());
    positions.push(...leftHere.toArray(), ...leftNext.toArray(), ...middleNext.toArray());
    positions.push(...middleHere.toArray(), ...rightNext.toArray(), ...rightHere.toArray());
    positions.push(...middleHere.toArray(), ...middleNext.toArray(), ...rightNext.toArray());
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  return geometry;
}

function bankGeometry(
  outerLeft: readonly THREE.Vector3[],
  innerLeft: readonly THREE.Vector3[],
  innerRight: readonly THREE.Vector3[],
  outerRight: readonly THREE.Vector3[],
): THREE.BufferGeometry {
  const positions: number[] = [];
  for (let index = 0; index < SEGMENTS; index += 1) {
    const outerLeftHere = outerLeft[index];
    const outerLeftNext = outerLeft[index + 1];
    const innerLeftHere = innerLeft[index];
    const innerLeftNext = innerLeft[index + 1];
    const innerRightHere = innerRight[index];
    const innerRightNext = innerRight[index + 1];
    const outerRightHere = outerRight[index];
    const outerRightNext = outerRight[index + 1];
    if (!outerLeftHere || !outerLeftNext || !innerLeftHere || !innerLeftNext ||
      !innerRightHere || !innerRightNext || !outerRightHere || !outerRightNext) {
      throw new Error('ditch-water: missing bank point');
    }
    positions.push(...outerLeftHere.toArray(), ...innerLeftNext.toArray(), ...innerLeftHere.toArray());
    positions.push(...outerLeftHere.toArray(), ...outerLeftNext.toArray(), ...innerLeftNext.toArray());
    positions.push(...innerRightHere.toArray(), ...outerRightNext.toArray(), ...outerRightHere.toArray());
    positions.push(...innerRightHere.toArray(), ...innerRightNext.toArray(), ...outerRightNext.toArray());
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  return geometry;
}

/** Build a deterministic channel section; the default seed is 83. */
export function buildDitchWater(options: DitchWaterOptions = {}): THREE.Group {
  const random = mulberry32(options.seed ?? 83);
  const group = new THREE.Group();
  group.name = 'ditch-water';
  const raw = Array.from({ length: SEGMENTS + 1 }, (_, index) =>
    index === 0 || index === SEGMENTS ? 0 : (random() - 0.5) * 0.24,
  );
  // The puddle's weighted neighbour smoothing, adapted to a line. End values
  // remain exactly zero after it so adjacent straight-end sections share banks.
  const lobes = raw.map((value, index) => {
    if (index === 0 || index === SEGMENTS) return 0;
    const before = raw[index - 1] ?? value;
    const after = raw[index + 1] ?? value;
    return (before + value * 2 + after) / 4;
  });
  const leftEdges: THREE.Vector3[] = [];
  const rightEdges: THREE.Vector3[] = [];
  const deepLeft: THREE.Vector3[] = [];
  const deepRight: THREE.Vector3[] = [];
  const shallowLeft: THREE.Vector3[] = [];
  const shallowRight: THREE.Vector3[] = [];
  for (let index = 0; index <= SEGMENTS; index += 1) {
    const x = -HALF_LENGTH + index * (HALF_LENGTH * 2 / SEGMENTS);
    const lobe = lobes[index] ?? 0;
    const edge = EDGE_HALF_WIDTH + lobe;
    const deep = edge * 0.45;
    const shallow = edge * 0.78;
    leftEdges.push(new THREE.Vector3(x, rise(1), -edge));
    rightEdges.push(new THREE.Vector3(x, rise(1), edge));
    deepLeft.push(new THREE.Vector3(x, rise(0.45), -deep));
    deepRight.push(new THREE.Vector3(x, rise(0.45), deep));
    shallowLeft.push(new THREE.Vector3(x, rise(0.78), -shallow));
    shallowRight.push(new THREE.Vector3(x, rise(0.78), shallow));
  }
  const bands = [
    { geometry: meniscusGeometry(deepLeft, deepRight), kind: 'wet' as const, colour: 0x22333a, name: 'deep-water' },
    { geometry: stripGeometry(shallowLeft, shallowRight), kind: 'wet' as const, colour: 0x2f4750, name: 'shallow-water' },
    { geometry: bankGeometry(leftEdges, shallowLeft, shallowRight, rightEdges), kind: 'organic' as const, colour: 0x2f4436, name: 'damp-bank' },
  ] as const;
  for (const band of bands) {
    const mesh = new THREE.Mesh(band.geometry, wetMaterial(band.kind, band.colour));
    mesh.name = band.name;
    mesh.castShadow = false;
    mesh.receiveShadow = true;
    group.add(mesh);
  }
  const bounds = new THREE.Box3().setFromObject(group);
  // X is symmetric by construction. Only separately randomised banks need this
  // measured Z shift to make a placement origin honest for every seed.
  group.position.z = -(bounds.min.z + bounds.max.z) / 2;
  return group;
}

export const ditchWaterAsset: AssetDefinition = {
  id: 'ditch-water',
  label: 'Ditch water',
  description: 'A lobed, stepped wet channel with flush tile ends.',
  footprint: { width: 4, height: 0.006, length: 2.45 },
  create: (): THREE.Group => buildDitchWater(),
};
